/**
 * Estimate Compare — "Good / Better / Best" A/B view.
 * Load any 2 saved estimates for the same client and render a side-by-side delta.
 * Export a combined "Compare Variants" PDF the sales team can send to clients.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { GitCompareArrows, Download, ArrowRight, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { API } from '@/App';

const fmt = (v) => `Rs ${Math.round(+v || 0).toLocaleString('en-IN')}`;
const deltaRow = (a, b) => {
  const diff = (+b || 0) - (+a || 0);
  const pct = (+a || 0) > 0 ? (diff / (+a)) * 100 : 0;
  return { diff, pct };
};

export default function EstimateCompare() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [estimates, setEstimates] = useState([]);
  const [a, setA] = useState(searchParams.get('a') || '');
  const [b, setB] = useState(searchParams.get('b') || '');
  const [estA, setEstA] = useState(null);
  const [estB, setEstB] = useState(null);
  const [exporting, setExporting] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/estimates`, { headers }).then(r => setEstimates(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (a) axios.get(`${API}/estimates/${a}`, { headers }).then(r => setEstA(r.data)).catch(() => setEstA(null));
    else setEstA(null);
    if (b) axios.get(`${API}/estimates/${b}`, { headers }).then(r => setEstB(r.data)).catch(() => setEstB(null));
    else setEstB(null);
    const next = new URLSearchParams();
    if (a) next.set('a', a);
    if (b) next.set('b', b);
    setSearchParams(next, { replace: true });
  }, [a, b]);

  const summary = useMemo(() => {
    const extract = (e) => {
      if (!e) return null;
      const c = e.calculations || {};
      const cfg = e.config || {};
      return {
        name: e.name,
        modules: (cfg.kitchen?.modules || []).length,
        vendor: c.totalProjectCost || 0,
        client: e.total_cost || c.totalProjectCost || 0,
        material: c.carcassCost || 0,
        hardware: (c.hardwareCost || 0) + (c.shutterCost || 0),
        labour: c.totalLabor || 0,
        adminMarkup: cfg.adminMarkup ?? 15,
        clientMarkup: cfg.clientMarkup ?? 25,
        shutter: cfg.kitchen?.defaults?.shutter || cfg.kitchen?.defaults?.shutter || '—',
        carcass: cfg.kitchen?.defaults?.carcass || '—',
      };
    };
    return { a: extract(estA), b: extract(estB) };
  }, [estA, estB]);

  const renderDelta = (va, vb) => {
    const d = deltaRow(va, vb);
    if (Math.abs(d.diff) < 1) return <span className="text-stone-400 inline-flex items-center gap-1"><Minus size={11} /> No change</span>;
    const up = d.diff > 0;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? 'text-rose-600' : 'text-emerald-600'}`}>
        {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{up ? '+' : ''}{fmt(d.diff)} ({d.pct.toFixed(1)}%)
      </span>
    );
  };

  const exportPDF = async () => {
    if (!estA || !estB) { toast.error('Pick both variants first'); return; }
    try {
      setExporting(true);
      const res = await axios.post(`${API}/estimates/compare-pdf`, {
        estimate_a_id: a, estimate_b_id: b,
      }, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Compare_${estA.name}_vs_${estB.name}.pdf`.replace(/\s+/g, '_');
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Compare PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate compare PDF');
    } finally { setExporting(false); }
  };

  const Row = ({ label, va, vb, bold, kind = 'money' }) => {
    const format = (v) => {
      if (kind === 'count') return Math.round(+v || 0).toString();
      if (kind === 'pct') return `${(+v || 0).toFixed(1)}%`;
      if (kind === 'text') return v || '—';
      return fmt(v);
    };
    const dlt = kind === 'money' ? (summary.a && summary.b ? renderDelta(va, vb) : '—') : (kind === 'count' ? `${(+vb || 0) - (+va || 0)}` : (kind === 'pct' ? `${(((+vb || 0) - (+va || 0))).toFixed(1)}%` : '—'));
    return (
      <tr className={bold ? 'border-t-2 border-stone-300' : ''}>
        <td className={`py-2 text-sm ${bold ? 'font-bold text-stone-900' : 'text-stone-600'}`}>{label}</td>
        <td className={`py-2 text-sm text-right font-mono ${bold ? 'font-bold' : ''}`}>{summary.a ? format(va) : '—'}</td>
        <td className={`py-2 text-sm text-right font-mono ${bold ? 'font-bold' : ''}`}>{summary.b ? format(vb) : '—'}</td>
        <td className="py-2 text-right text-xs">{dlt}</td>
      </tr>
    );
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-6" data-testid="estimate-compare-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2"><GitCompareArrows size={22} className="text-purple-600" />Compare Variants (Good / Better / Best)</h1>
          <p className="text-sm text-stone-500 mt-0.5">Side-by-side A/B comparison of any two saved estimates — ideal for presenting packaged upgrades to clients.</p>
        </div>
        <Button onClick={exportPDF} disabled={!estA || !estB || exporting} data-testid="compare-export-pdf-btn">
          <Download size={15} className="mr-1" />{exporting ? 'Generating...' : 'Export Compare PDF'}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Variant A (base)</Label>
              <select className="h-10 w-full rounded-md border border-stone-300 px-3 text-sm" value={a} onChange={e => setA(e.target.value)} data-testid="variant-a-select">
                <option value="">— Select —</option>
                {estimates.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Variant B (comparison)</Label>
              <select className="h-10 w-full rounded-md border border-stone-300 px-3 text-sm" value={b} onChange={e => setB(e.target.value)} data-testid="variant-b-select">
                <option value="">— Select —</option>
                {estimates.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {(estA || estB) && (
        <Card>
          <CardContent className="pt-5 pb-4">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-stone-200">
                  <th className="py-2 text-left text-xs font-semibold text-stone-500 uppercase">Metric</th>
                  <th className="py-2 text-right text-xs font-semibold text-stone-500 uppercase">{summary.a?.name || 'Variant A'}</th>
                  <th className="py-2 text-right text-xs font-semibold text-stone-500 uppercase">{summary.b?.name || 'Variant B'}</th>
                  <th className="py-2 text-right text-xs font-semibold text-stone-500 uppercase">Delta (B vs A)</th>
                </tr>
              </thead>
              <tbody>
                <Row label="Modules" va={summary.a?.modules || 0} vb={summary.b?.modules || 0} kind="count" />
                <Row label="Material" va={summary.a?.material || 0} vb={summary.b?.material || 0} />
                <Row label="Hardware + Shutter" va={summary.a?.hardware || 0} vb={summary.b?.hardware || 0} />
                <Row label="Labour" va={summary.a?.labour || 0} vb={summary.b?.labour || 0} />
                <Row label="Admin Markup %" va={summary.a?.adminMarkup || 0} vb={summary.b?.adminMarkup || 0} kind="pct" />
                <Row label="Client Markup %" va={summary.a?.clientMarkup || 0} vb={summary.b?.clientMarkup || 0} kind="pct" />
                <Row label="Vendor / Raw Cost" va={summary.a?.vendor || 0} vb={summary.b?.vendor || 0} bold />
                <Row label="Final Client Price" va={summary.a?.client || 0} vb={summary.b?.client || 0} bold />
              </tbody>
            </table>
            {estA && estB && (
              <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-3">
                <GitCompareArrows size={16} className="text-purple-600 mt-0.5" />
                <div className="text-xs text-purple-900">
                  <p className="font-semibold mb-0.5">Summary</p>
                  <p>Variant B is {deltaRow(summary.a?.client, summary.b?.client).diff > 0 ? 'higher' : 'lower'} by {fmt(Math.abs(deltaRow(summary.a?.client, summary.b?.client).diff))} ({deltaRow(summary.a?.client, summary.b?.client).pct.toFixed(1)}%) compared to Variant A.</p>
                </div>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              {a && <Link to={`/estimators/${(estA?.config?.productType === 'kitchen') ? 'kitchen' : 'wardrobe'}/${a}`} className="text-xs text-purple-700 hover:underline flex items-center gap-1">Open Variant A <ArrowRight size={11} /></Link>}
              {b && <Link to={`/estimators/${(estB?.config?.productType === 'kitchen') ? 'kitchen' : 'wardrobe'}/${b}`} className="text-xs text-purple-700 hover:underline flex items-center gap-1 ml-4">Open Variant B <ArrowRight size={11} /></Link>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
